import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram } from "lucide-react";

const CATEGORIES = ["ALLE", "BLACKWORK", "REALISTISCH", "FARBE", "FINELINE", "TRADITIONAL", "KLEIN", "PIERCING"];

const PORTFOLIO = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1564426622559-5af68da63b96?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    artist: "Franco",
    category: "BLACKWORK",
    title: "Blackwork Sleeve",
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1561377455-190afb395ed7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    artist: "David",
    category: "BLACKWORK",
    title: "Full Back Blackwork",
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1605647533135-51b5906087d0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    artist: "Rooney",
    category: "REALISTISCH",
    title: "Portrait Realistisch",
  },
  {
    id: 4,
    image: "https://images.unsplash.com/photo-1565058379802-bbe93b2f703a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    artist: "Marcel",
    category: "REALISTISCH",
    title: "Realistisches Motiv",
  },
  {
    id: 5,
    image: "https://images.pexels.com/photos/12509434/pexels-photo-12509434.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    artist: "Kenan",
    category: "FARBE",
    title: "Farb-Tattoo",
  },
  {
    id: 6,
    image: "https://images.pexels.com/photos/2928342/pexels-photo-2928342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    artist: "Sascha",
    category: "TRADITIONAL",
    title: "Traditional Style",
  },
  {
    id: 7,
    image: "https://images.unsplash.com/photo-1597852075234-fd721ac361d3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    artist: "Marcel",
    category: "FINELINE",
    title: "Fineline Design",
  },
  {
    id: 8,
    image: "https://images.pexels.com/photos/5088486/pexels-photo-5088486.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    artist: "David",
    category: "FINELINE",
    title: "Fine Line Blatt",
  },
  {
    id: 9,
    image: "https://images.pexels.com/photos/7901490/pexels-photo-7901490.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    artist: "Franco",
    category: "KLEIN",
    title: "Kleines Tattoo",
  },
  {
    id: 10,
    image: "https://images.pexels.com/photos/36654932/pexels-photo-36654932.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    artist: "Rooney",
    category: "BLACKWORK",
    title: "Blüten Blackwork",
  },
  {
    id: 11,
    image: "https://images.unsplash.com/photo-1607281503082-f01fedd97a5b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    artist: "Lilly",
    category: "PIERCING",
    title: "Piercing Design",
  },
  {
    id: 12,
    image: "https://images.pexels.com/photos/11364054/pexels-photo-11364054.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    artist: "Kenan",
    category: "BLACKWORK",
    title: "Dark Tattoo",
  },
];

export default function PortfolioSection() {
  const [activeFilter, setActiveFilter] = useState("ALLE");

  const filtered =
    activeFilter === "ALLE"
      ? PORTFOLIO
      : PORTFOLIO.filter((item) => item.category === activeFilter);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <span className="section-label">Galerie</span>
          <h2 className="section-heading">Unsere Arbeiten.</h2>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          className="flex flex-wrap gap-2 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`portfolio-filter-${cat.toLowerCase()}`}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 text-xs tracking-[0.15em] uppercase font-outfit transition-all duration-300 ${
                activeFilter === cat
                  ? "bg-[#C9A84C] text-[#0A0A0A] font-semibold"
                  : "border border-[#5A4A1A]/60 text-[#888888] hover:border-[#C9A84C]/60 hover:text-[#C9A84C]"
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Masonry Grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((item, i) => (
              <motion.div
                key={item.id}
                data-testid={`portfolio-item-${item.id}`}
                className="break-inside-avoid group relative overflow-hidden bg-[#141414] border border-[#5A4A1A]/20 cursor-pointer"
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-[#0A0A0A]/0 group-hover:bg-[#0A0A0A]/70 transition-all duration-400 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100">
                  <p className="text-[#C9A84C] text-xs tracking-[0.2em] uppercase font-outfit mb-1">
                    {item.category}
                  </p>
                  <p className="text-[#F0F0F0] font-cormorant text-xl italic">
                    {item.title}
                  </p>
                  <p className="text-[#888888] text-xs mt-1 font-outfit">
                    von {item.artist}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#888888] text-sm font-outfit mb-6">
            Mehr Werke auf Instagram
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://instagram.com/royal.tattoo.grevenbroich"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-gv-link"
              className="gold-btn flex items-center gap-2"
            >
              <Instagram size={15} />
              @royal.tattoo.grevenbroich
            </a>
            <a
              href="https://instagram.com/royal.tattoo.leverkusen"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-lev-link"
              className="ghost-btn flex items-center gap-2"
            >
              <Instagram size={15} />
              @royal.tattoo.leverkusen
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
